import { ArrowRight, Calendar } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { UseCaseHero } from "../types"

interface DetailHeroSectionProps {
  /** Hero content configuration */
  hero: UseCaseHero
}

/**
 * Hero section for Use Case detail pages.
 * Displays badge, headline, subheadline, and CTA buttons.
 */
export function DetailHeroSection({ hero }: DetailHeroSectionProps) {
  const navigate = useNavigate()

  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <Badge className="mb-6 border-primary/30 bg-primary/10 text-primary">
            {hero.badge}
          </Badge>
          <h1 className="mb-6 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            {hero.headline}
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-neutral-400">
            {hero.subheadline}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="group bg-primary text-white hover:bg-primary/90"
              onClick={() => navigate("/signup")}
            >
              Start Building
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
              onClick={() => navigate("/demo")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Demo
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
